import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import axios from "../../utils/axiosInstance";
import {
  getStateDescriptions,
  isCustomerError,
} from "../../utils/things/PRECISION";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function RuntimeReport({ device }) {
  const [expandedReportRow, setExpandedReportRow] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [reportsPage, setReportsPage] = useState(0);
  const rowsPerPageReports = 10;
  const [SDPage, setSDPage] = useState(0);
  const rowsPerPageSD = 17;

  const [PRCTotal, setPRCTotal] = useState(0);
  const [custTotal, setCustTotal] = useState(0);
  const [uptimePercent, setUptimePercent] = useState(0);
  const [datapoints, setDatapoints] = useState(0);
  const [searchReports, setSearchReports] = useState("");
  const [searchSDs, setSearchSDs] = useState("");

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [openDateDialog, setOpenDateDialog] = useState(false);

  const [sortConfigReports, setSortConfigReports] = useState({
    key: "Date Start",
    direction: "desc",
  });
  const [sortConfigSDs, setSortConfigSDs] = useState({
    key: "Date ",
    direction: "desc",
  });

  // Fetch engine data first
  useEffect(() => {
    const fetchEngineData = async () => {
      if (!device?.id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/engine-data/${device.id}`,
        );
        if (!response)
          throw new Error(`HTTP error! status: ${response.status}`);
        setData(response.data);
      } catch (err) {
        console.error("Error fetching engine data:", err);
        setData(null);
      }
    };

    fetchEngineData();
  }, [device?.id]);

  const fetchReport = async (year = selectedYear, month = selectedMonth) => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_API_GATEAWAY}/reports/externalruntimereport/${device.id}/${year}/${month}`;
      const response = await axios.get(url);
      setReportData(response.data.response);
      setError(null);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch runtime report after engine data is loaded
  useEffect(() => {
    if (!device?.id || !data) return;

    const fetchReport = async () => {
      try {
        setLoading(true);

        const url = `${process.env.REACT_APP_API_GATEAWAY}/reports/externalruntimereport/${device.id}/${selectedYear}/${selectedMonth}`;

        const response = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        setReportData(response.data.response);

        setError(null);
      } catch (err) {
        console.error("Error fetching report:", err);
        if (err.response?.status === 429) {
          setError("Rate limit exceeded. Please wait a moment and try again.");
        } else {
          setError("Failed to load report data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [device?.id, data]);

  // Memoize the statistics calculation from the new API response
  const calculatedRows = useMemo(() => {
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      return { rows: [], PRCTotalCalc: 0, custTotalCalc: 0 };
    }

    let PRCTotalCalc = 0;
    let custTotalCalc = 0;

    // Calculate totals from the pre-calculated downtime events
    reportData.forEach((item) => {
      const companyTime = parseFloat(item.companyTime) || 0;
      const customerTime = parseFloat(item.customerTime) || 0;

      PRCTotalCalc += companyTime;
      custTotalCalc += customerTime;
    });

    return { rows: [], PRCTotalCalc, custTotalCalc };
  }, [reportData]);

  const sortedReports = useMemo(() => {
    if (!reportData || typeof reportData !== "object") return [];

    const mapped = Object.values(reportData).map((item) => ({
      // RAW timestamps (for sorting)
      timeStartTs: item.timeStart,
      timeEndTs: item.timeEnd,

      // DISPLAY values
      "Date Start": item.timeStart
        ? new Date(item.timeStart * 1000).toLocaleString()
        : "-",
      "Date End": item.timeEnd
        ? new Date(item.timeEnd * 1000).toLocaleString()
        : "-",

      "Shutdown Code": item.faultCode,
      "Shutdown Enum": item.fault,
      "Downtime (Hrs)": item.downtime,
      "Customer (Hrs)": item.customerTime,
      "PRC (Hrs)": item.companyTime,
    }));

    if (!sortConfigReports?.key) return mapped;

    const { key, direction } = sortConfigReports;
    const dir = direction === "asc" ? 1 : -1;

    return [...mapped].sort((a, b) => {
      // ✅ TIMESTAMP SORTING
      if (key === "Date Start") {
        return (a.timeStartTs - b.timeStartTs) * dir;
      }

      if (key === "Date End") {
        return (a.timeEndTs - b.timeEndTs) * dir;
      }

      const valA = a[key];
      const valB = b[key];

      // numeric sorting
      if (typeof valA === "number" && typeof valB === "number") {
        return (valA - valB) * dir;
      }

      // fallback string sorting
      return String(valA ?? "").localeCompare(String(valB ?? "")) * dir;
    });
  }, [reportData, sortConfigReports]);

  //filtered rows for reports table
  const filteredReports = useMemo(() => {
    if (!sortedReports?.length) return [];
    if (!searchReports) return sortedReports;

    const query = searchReports.toLowerCase();

    return sortedReports.filter((row) =>
      Object.values(row).some(
        (val) => val != null && String(val).toLowerCase().includes(query),
      ),
    );
  }, [searchReports, sortedReports]);

  // Update totals when calculated rows change
  useEffect(() => {
    setPRCTotal(calculatedRows.PRCTotalCalc || 0);
    setCustTotal(calculatedRows.custTotalCalc || 0);
    setDatapoints(reportData?.length || 0);

    // Calculate uptime percentage based on the time range in reportData
    if (reportData && Array.isArray(reportData) && reportData.length > 0) {
      // Find the earliest timeStart and latest timeEnd
      const times = reportData
        .map((item) => ({
          start: item.timeStart,
          end: item.timeEnd,
        }))
        .filter((t) => t.start && t.end);

      if (times.length > 0) {
        const earliestStart = Math.min(...times.map((t) => t.start));
        const latestEnd = Math.max(...times.map((t) => t.end));
        const totalHours = (latestEnd - earliestStart) / 3600;

        const totalDowntime =
          calculatedRows.custTotalCalc + calculatedRows.PRCTotalCalc;
        const uptime =
          totalHours > 0
            ? (100 * (totalHours - totalDowntime)) / totalHours
            : 0;
        setUptimePercent(uptime);
      } else {
        setUptimePercent(0);
      }
    } else {
      setUptimePercent(0);
    }
  }, [calculatedRows, reportData]);

  const getRowsSD = useMemo(() => {
    if (!reportData || !Array.isArray(reportData)) return [];

    return reportData.map((item) => {
      return {
        timeTs: item.time,
        "Date ": new Date(item.time * 1000).toLocaleString(),
        "Shutdown Code": item.faultCode ?? "-",
      };
    });
  }, [reportData]);

  //filtered rows for SDs table
  const sortedSDs = useMemo(() => {
    if (!getRowsSD?.length) return [];
    let sortable = [...getRowsSD];
    if (sortConfigSDs.key) {
      const { key, direction } = sortConfigSDs;
      const dir = direction === "asc" ? 1 : -1;

      sortable.sort((a, b) => {
        if (key === "Date ") {
          return ((a.timeTs || 0) - (b.timeTs || 0)) * dir;
        }
        const valA = a[key];
        const valB = b[key];
        if (!isNaN(valA) && !isNaN(valB)) {
          return (Number(valA) - Number(valB)) * dir;
        }
        return String(valA ?? "").localeCompare(String(valB ?? "")) * dir;
      });
    }
    return sortable;
  }, [getRowsSD, sortConfigSDs]);

  const filteredSDs = useMemo(() => {
    if (!sortedSDs.length) return [];
    if (!searchSDs) return sortedSDs;
    const query = searchSDs.toLowerCase().trim();
    return sortedSDs.filter((row) =>
      Object.values(row).some((val) => {
        const strVal = String(val).toLowerCase();
        // Check for exact match or match whole words
        return (
          strVal === query || strVal.split(/\s+/).some((word) => word === query)
        );
      }),
    );
  }, [searchSDs, sortedSDs]);

  // extract in files
  //reports download handlers
  const handleDownloadExcelReports = () => {
    // Remove timeStartTs and timeEndTs fields from export
    const cleanedData = filteredReports.map(
      ({ timeStartTs, timeEndTs, ...rest }) => rest,
    );
    const worksheet = XLSX.utils.json_to_sheet(cleanedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `Reports_${device?.name || "device"}.xlsx`);
  };

  const handleDownloadCSVReports = () => {
    // Remove timeStartTs and timeEndTs fields from export
    const cleanedData = filteredReports.map(
      ({ timeStartTs, timeEndTs, ...rest }) => rest,
    );
    const worksheet = XLSX.utils.json_to_sheet(cleanedData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Reports_${device?.name || "device"}.csv`);
  };

  const headersReports = [
    "Date Start",
    "Date End",
    "Shutdown Code",
    "Shutdown Enum",
    "Downtime (Hrs)",
    "Customer (Hrs)",
    "PRC (Hrs)",
  ];
  const handleDownloadPDFReports = () => {
    const rows = filteredReports;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text(`Reports For Device : ${device?.name}`, 14, 10);
    autoTable(doc, {
      head: [headersReports],
      body: rows.map((row) => headersReports.map((h) => row[h])),
      startY: 20,
      styles: { fontSize: 6 },
    });
    doc.save(`Reports_${device?.name || "device"}.pdf`);
  };
  //shutdowns download handlers
  const handleDownloadExcelSDs = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredSDs);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `SD_Codes_${device?.name || "device"}.xlsx`);
  };
  const handleDownloadCSVSDSs = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredSDs);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `SD_Codes_${device?.name || "device"}.csv`);
  };
  const headersSDs = ["Date ", "Shutdown Code"];
  const handleDownloadPDFSDs = () => {
    const rows = filteredSDs;
    const doc = new jsPDF({ orientation: "portrait" });
    doc.text(`SD Codes For Device : ${device?.id}`, 14, 10);
    autoTable(doc, {
      head: [headersSDs],
      body: rows.map((row) => headersSDs.map((h) => row[h])),
      startY: 20,
      styles: { fontSize: 6 },
    });
    doc.save(`SD_Codes_${device?.name || "device"}.pdf`);
  };

  if (loading) return <div className="p-3 text-white">Loading report...</div>;
  if (error) return <div className="p-3 text-red-500">{error}</div>;

  return (
    <>
      {/* Date Selection Dialog */}
      {openDateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg p-6 w-96 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4">
              Select Report Period
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-slate-300 mb-2">
                  Select Year:
                </label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="w-full bg-slate-700 text-white border border-slate-600 rounded px-3 py-2"
                >
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-300 mb-2">
                  Select Month:
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="w-full bg-slate-700 text-white border border-slate-600 rounded px-3 py-2"
                >
                  {[
                    { num: 1, name: "January" },
                    { num: 2, name: "February" },
                    { num: 3, name: "March" },
                    { num: 4, name: "April" },
                    { num: 5, name: "May" },
                    { num: 6, name: "June" },
                    { num: 7, name: "July" },
                    { num: 8, name: "August" },
                    { num: 9, name: "September" },
                    { num: 10, name: "October" },
                    { num: 11, name: "November" },
                    { num: 12, name: "December" },
                  ].map((m) => (
                    <option key={m.num} value={m.num}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setOpenDateDialog(false)}
                className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setOpenDateDialog(false);
                  fetchReport();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 bg-slate-900 min-h-screen">
        {/* Summary bar */}
        <div className="hidden sm:flex w-full flex-col sm:flex-row sm:flex-wrap justify-between gap-3 sm:gap-6 mb-6 text-slate-300 text-sm">
          <div className="w-full sm:w-auto">
            Monthly PRC (Hours): {PRCTotal.toFixed(2)}
          </div>
          <div className="w-full sm:w-auto">
            Monthly Customer (Hours): {custTotal.toFixed(2)}
          </div>
          <div className="w-full sm:w-auto">
            Monthly PRC (%):{" "}
            {custTotal + PRCTotal === 0
              ? "No Downtime"
              : ((100 * PRCTotal) / (custTotal + PRCTotal)).toFixed(2)}
          </div>
          <div className="w-full sm:w-auto">
            Monthly Customer (%):{" "}
            {custTotal + PRCTotal === 0
              ? "No Downtime"
              : ((100 * custTotal) / (custTotal + PRCTotal)).toFixed(2)}
          </div>
          <div className="w-full sm:w-auto">
            Monthly Uptime (%):{" "}
            {datapoints === 0 ? "No Data From Unit" : uptimePercent.toFixed(1)}
          </div>
          <div className="w-full sm:w-auto">Datapoints: {datapoints}</div>
        </div>

        {/* Tables container */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Table (70%) */}
          <div className="col-span-2 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            {/* Header */}
            {/* Header with title and actions */}
            <div className="px-4 py-3 bg-slate-900 border-b border-slate-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              {/* Title */}
              <h3 className="text-white font-semibold text-base sm:text-lg ">
                {device?.name || "Device"} : Reports for {selectedMonth}/
                {selectedYear}
              </h3>

              {/* Action buttons */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-row gap-2">
                  <button
                    onClick={() => setOpenDateDialog(true)}
                    className="px-3 py-1 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 flex-1 sm:flex-auto"
                  >
                    Select Date
                  </button>
                  {/* <button
                    onClick={() => {
                      const now = new Date();
                      const year = now.getFullYear();
                      const month = now.getMonth() + 1;
                      setSelectedYear(year);
                      setSelectedMonth(month);
                      fetchReport(year, month);
                    }}
                    className="px-3 py-1 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 flex-1 sm:flex-auto"
                  >
                    Detailed
                  </button> */}
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="px-4 py-3 bg-slate-900 border-b border-slate-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search..."
                value={searchReports}
                onChange={(e) => {
                  setSearchReports(e.target.value);
                  setReportsPage(0);
                }}
                className="px-3 py-1 bg-slate-700 text-white border border-slate-600 rounded text-sm w-full sm:w-48"
              />

              {/* Download buttons */}
              <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
                <button
                  onClick={handleDownloadExcelReports}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  Excel
                </button>
                <button
                  onClick={handleDownloadCSVReports}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  CSV
                </button>
                <button
                  onClick={handleDownloadPDFReports}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  PDF
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    {/* Mobile expand column */}
                    <th className="px-3 py-2 lg:hidden w-8"></th>

                    {headersReports.map((header) => (
                      <th
                        key={header}
                        onClick={() =>
                          setSortConfigReports((prev) => ({
                            key: header,
                            direction:
                              prev.key === header && prev.direction === "asc"
                                ? "desc"
                                : "asc",
                          }))
                        }
                        className={`px-3 py-2.5 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer select-none
          ${header !== "Shutdown Enum" ? "hidden lg:table-cell" : ""}`}
                      >
                        {header}{" "}
                        {sortConfigReports.key === header
                          ? sortConfigReports.direction === "asc"
                            ? "▲"
                            : "▼"
                          : "↕"}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-700">
                  {filteredReports
                    .slice(
                      reportsPage * rowsPerPageReports,
                      reportsPage * rowsPerPageReports + rowsPerPageReports,
                    )
                    .map((row, idx) => {
                      const isOpen = expandedReportRow === idx;

                      return (
                        <Fragment key={idx}>
                          {/* MAIN ROW */}
                          <tr
                            className={`text-gray-200 ${
                              idx % 2 === 0
                                ? "bg-slate-800 hover:bg-slate-700"
                                : "bg-slate-900 hover:bg-slate-700"
                            }`}
                          >
                            {/* Arrow (mobile only) */}
                            <td className="px-3 py-2.5 lg:hidden text-center">
                              <button
                                onClick={() =>
                                  setExpandedReportRow(isOpen ? null : idx)
                                }
                                className="text-slate-300"
                              >
                                {isOpen ? (
                                  <ChevronDown size={18} />
                                ) : (
                                  <ChevronRight size={18} />
                                )}
                              </button>
                            </td>

                            {/* Date Start */}
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300 whitespace-nowrap">
                              {row["Date Start"]}

                              {/* {row?.timeStart} */}
                            </td>

                            {/* Date End */}
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300 whitespace-nowrap">
                              {row["Date End"]}
                              {/* {row?.timeEnd} */}
                            </td>

                            {/* Shutdown Code */}
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300">
                              {row["Shutdown Code"]}
                              {/* {row?.faultCode} */}
                            </td>

                            {/* Shutdown Enum (always visible) */}
                            <td className="px-3 py-2.5 text-slate-300">
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-700 text-slate-200 border border-slate-600">
                                {row["Shutdown Enum"]}
                                {/* {row?.fault} */}
                              </span>
                            </td>

                            {/* Numbers */}
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300 font-mono text-center">
                              {row["Downtime (Hrs)"]}
                              {/* {row?.downtime} */}
                            </td>
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300 text-center font-mono">
                              {row["Customer (Hrs)"]}
                              {/* {row?.customerTime} */}
                            </td>
                            <td className="hidden lg:table-cell px-3 py-2.5 text-slate-300 text-center font-mono">
                              {row["PRC (Hrs)"]}
                              {/* {row?.companyTime} */}
                            </td>
                          </tr>

                          {/* EXPANDED CONTENT (mobile only) */}
                          {isOpen && (
                            <tr className="lg:hidden bg-slate-900">
                              <td
                                colSpan={2}
                                className="px-4 py-3 text-sm text-slate-300"
                              >
                                <div className="space-y-2">
                                  <div>
                                    <span className="text-slate-400">
                                      Date Start:
                                    </span>{" "}
                                    {row["Date Start"]}
                                  </div>
                                  <div>
                                    <span className="text-slate-400">
                                      Date End:
                                    </span>{" "}
                                    {row["Date End"]}
                                  </div>
                                  <div>
                                    <span className="text-slate-400">
                                      Shutdown Code:
                                    </span>{" "}
                                    {row["Shutdown Code"]}
                                  </div>
                                  <div>
                                    <span className="text-slate-400">
                                      Downtime (Hrs):
                                    </span>{" "}
                                    {row["Downtime (Hrs)"]}
                                  </div>
                                  <div>
                                    <span className="text-slate-400">
                                      Customer (Hrs):
                                    </span>{" "}
                                    {row["Customer (Hrs)"]}
                                  </div>
                                  <div>
                                    <span className="text-slate-400">
                                      PRC (Hrs):
                                    </span>{" "}
                                    {row["PRC (Hrs)"]}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Footer with pagination */}
            <div className="px-4 py-3 bg-slate-900 border-t border-slate-700 flex justify-between items-center">
              <button
                disabled={reportsPage === 0}
                onClick={() => setReportsPage(reportsPage - 1)}
                className="px-4 py-2 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {reportsPage + 1} of{" "}
                {Math.ceil(filteredReports.length / rowsPerPageReports) || 1}
              </span>
              <button
                disabled={
                  reportsPage >=
                  Math.ceil(filteredReports.length / rowsPerPageReports) - 1
                }
                onClick={() => setReportsPage(reportsPage + 1)}
                className="px-4 py-2 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>

          {/* Right Table (30%) */}
          <div className="col-span-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 bg-slate-900 border-b border-slate-700">
              <h3 className="text-white font-semibold">
                {device?.name || "Device"} : SD Code for {selectedMonth}/
                {selectedYear}
              </h3>
            </div>

            {/* Action buttons */}
            <div className="px-4 py-3 bg-slate-900 border-b border-slate-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search..."
                value={searchSDs}
                onChange={(e) => {
                  setSearchSDs(e.target.value);
                  setSDPage(0);
                }}
                className="px-3 py-1 bg-slate-700 text-white border border-slate-600 rounded text-sm w-full sm:w-32"
              />

              {/* Download buttons */}
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <button
                  onClick={handleDownloadExcelSDs}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  Excel
                </button>
                <button
                  onClick={handleDownloadCSVSDSs}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  CSV
                </button>
                <button
                  onClick={handleDownloadPDFSDs}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex-1 sm:flex-auto"
                >
                  PDF
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    {headersSDs.map((header) => (
                      <th
                        key={header}
                        onClick={() =>
                          setSortConfigSDs((prev) => ({
                            key: header,
                            direction:
                              prev.key === header && prev.direction === "asc"
                                ? "desc"
                                : "asc",
                          }))
                        }
                        className="px-3 py-2.5 text-center text-xs font-semibold text-slate-300 uppercase tracking-wider cursor-pointer select-none"
                      >
                        {header}{" "}
                        {sortConfigSDs.key === header
                          ? sortConfigSDs.direction === "asc"
                            ? "▲"
                            : "▼"
                          : "↕"}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredSDs
                    .slice(
                      SDPage * rowsPerPageSD,
                      SDPage * rowsPerPageSD + rowsPerPageSD,
                    )
                    .map((row, idx) => (
                      <tr
                        key={idx}
                        className={`text-gray-200 ${
                          idx % 2 === 0
                            ? "bg-slate-800 hover:bg-slate-700"
                            : "bg-slate-900 hover:bg-slate-700"
                        }`}
                      >
                        <td className="text-xs sm:text-sm px-3 py-2.5 text-center text-slate-300">
                          {row["Date "]}
                        </td>
                        <td className="px-3 py-2.5 text-slate-300 text-center font-mono">
                          {row["Shutdown Code"]}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Footer with pagination */}
            <div className="px-4 py-3 bg-slate-900 border-t border-slate-700 flex justify-between items-center">
              <button
                disabled={SDPage === 0}
                onClick={() => setSDPage(SDPage - 1)}
                className="px-4 py-2 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {SDPage + 1} of{" "}
                {Math.ceil(filteredSDs.length / rowsPerPageSD) || 1}
              </span>
              <button
                disabled={
                  SDPage >= Math.ceil(filteredSDs.length / rowsPerPageSD) - 1
                }
                onClick={() => setSDPage(SDPage + 1)}
                className="px-4 py-2 bg-slate-700 text-white text-sm rounded hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
